import { apiInstance } from '@/api/api.js'
const api = apiInstance()
import { debounce } from '@/helpers/debounce'

const LIMIT_GET_BY_PROFILE_ID = 25
const LIMIT_GET_BY_UUID = 25

export default {
  /**
   * Function which add elements to refresh profiles queue, after set new array, it runs debounced func to refresh profiles with provided data in this queue.
   * @param {any} - vuex state, commit, dispatch etc
   * @param {Object[]} queueItems as array of objects with props: uuid and profileId (one of them required)
   * @param {number | undefined | null} queueItems[].profileId - ProfileId of user to refresh
   * @param {string | undefined | null} queueItems[].uuid - Uuid of user to refresh
   */
  addItemsToProfilePreviewQueue({state, commit, dispatch}, queueItems) {
    commit('addProfilePreviewsQueue', queueItems)
    dispatch('getProfilePreviewsDebounced')
  },
  /**
   * Function which set elements in refresh profiles queue, after set new array, it runs debounced func to refresh profiles with provided data in this queue.
   * @param {any} - vuex state, commit, dispatch etc
   * @param {Object[]} queueItems as array of objects with props: uuid and profileId (one of them required)
   * @param {number | undefined | null} queueItems[].profileId - ProfileId of user to refresh
   * @param {string | undefined | null} queueItems[].uuid - Uuid of user to refresh
   */
  setItemsInProfilePreviewQueue({state, commit, dispatch}, queueItems) {
    commit('setProfilePreviewsQueue', queueItems)
    dispatch('getProfilePreviewsDebounced')
  },
  /**
   * Function which remove elements from refresh profiles queue, after set new array, it runs debounced func to refresh profiles with provided data in this queue.
   * @param {any} - vuex state, commit, dispatch etc
   * @param {Object[]} queueItems as array of objects with props: uuid and profileId (one of them required)
   * @param {number | undefined | null} queueItems[].profileId - ProfileId of user to refresh
   * @param {string | undefined | null} queueItems[].uuid - Uuid of user to refresh
   */
  removeItemsInProfilePreviewQueue({state, commit, dispatch}, queueItems) {
    const newQueueItems = state.profilePreviewsQueue.filter(
      currentQueueItem => {
        const elementExists = !!queueItems.find(
          removeQueueItem =>
            (removeQueueItem.profileId &&
              currentQueueItem.profileId === removeQueueItem.profileId) ||
            (removeQueueItem.uuid &&
              currentQueueItem.uuid === removeQueueItem.uuid),
        )
        return !elementExists
      },
    )
    commit('setProfilePreviewsQueue', newQueueItems)
    dispatch('getProfilePreviewsDebounced')
  },
  /**
   * Function which add/update profile previews in state and remove this profiles from refresh queue.
   * @param {any} - vuex state, commit, dispatch etc
   * @param {Object[]} profilePreviews - as array of objects (profile previews) returned from backend.
   * @param {string} profilePreviews[].firstName - first name
   * @param {string} profilePreviews[].lastName - last name
   * @param {string} profilePreviews[].fullName - full name
   * @param {string} profilePreviews[].nick - nick
   * @param {boolean} profilePreviews[].isActive - is active
   * @param {boolean} profilePreviews[].processingMedia - is thumbnail still processing
   * @param {string} profilePreviews[].thumbnailUrl - thumbnail url
   * @param {string} profilePreviews[].uuid - uuid
   * @param {number} profilePreviews[].profileId - profile id
   * @param {number} profilePreviews[].userId - user id
   * @param {number} profilePreviews[].roleId - role id
   * @param {boolean | undefined | null } profilePreviews[].journalAvailable - journal available
   * 
   * @link https://bitbucket.org/believe_health/backend/src/develop/src/main/resources/api/profile-management-v1.yaml - backend schema of ProfilePreview
   */
  setProfilePreviewsAction({state, commit, dispatch}, profilePreviews) {
    if (profilePreviews.length === 0) return
    commit('setProfilePreviews', profilePreviews)
    const itemsToRemoveFromQueue = profilePreviews.map(el => ({
      profileId: el.profileId,
      uuid: el.uuid
    }))
    if (itemsToRemoveFromQueue.length !== 0) {
      dispatch('removeItemsInProfilePreviewQueue', itemsToRemoveFromQueue)
    }
  },
  /**
   * Function to get profile previews and save it to state by setProfilePreviewAction
   */
  async getProfilePreviews({state, commit, dispatch}) {
    const queue = state.profilePreviewsQueue

    // Stop if no items to refresh
    if (queue.length === 0) return

    commit('setProfilePreviewsLoading', true)

    /**
     * @constant {{profileId: number}[]} - Array of objects with profile Id
     */
    const profileIds = queue.filter(el => !!el.profileId).map(el => el.profileId)
    /**
     * @constant {{profileId: number}[]} - Array of objects with profile Id
     */
    const profileIdsToGet = profileIds.splice(0, LIMIT_GET_BY_PROFILE_ID)
    /**
     * @constant {{uuid: string}[]} - Array of objects with uuid
     */
    const uuids = queue.filter(el => !!el.uuid).map(el => el.uuid)
    /**
     * @constant {{uuid: string}[]} - Array of objects with uuid
     */
    const uuidsToGet = uuids.splice(0, LIMIT_GET_BY_UUID)

    try {
      let url = null
      const params = new URLSearchParams()
      if (profileIdsToGet.length !== 0) {
        url = 'profiles/preview'
        profileIdsToGet.forEach((profileId) => {
          params.append('profileIds', profileId.toString())
        })
      } else if (uuidsToGet.length !== 0) {
        url = 'profiles/preview/uuid'
        uuidsToGet.forEach((uuid) => {
          params.append('profileUuids', uuid.toString())
        })
      }
      
      // Stop if there is no profileIds or uuids of elements to refresh
      if (url === null) return

      const newProfilePreviews = await api.get(url, { params })
      if (newProfilePreviews.data) {
        const profilePreviewsItems = newProfilePreviews.data.items || newProfilePreviews.data // TODO CHECK WHAT IS RETURN
        dispatch('setProfilePreviewsAction', profilePreviewsItems)
      }

      // If there is more items to get, then rerun this function
      if (profileIds.length > profileIdsToGet.length || uuids.length > uuidsToGet.length) {
        dispatch('getProfilePreviews')
      }
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
    } finally {
      commit('setProfilePreviewsLoading', false)
      dispatch('getProfilePreviewsDebounced')
    }
  },
  /**
   * Debounce function which runs getProfilePreviews after 2000ms since last call
   */
  async getProfilePreviewsDebounced({ dispatch }) {
    debounce(
      () => dispatch('getProfilePreviews'),
      2000,
      'getProfilePreviews',
    )
  },
}
